/**
 * @copyright Copyright (c) Open-Xchange GmbH, Germany <info@open-xchange.com>
 * @license AGPL-3.0
 *
 * This code is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with OX App Suite. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.
 *
 * Any use of the work other than as authorized under this license or copyright law is prohibited.
 */

import { settings as coreSettings } from '@/io.ox/core/settings'
import { settings as calendarSettings } from '@/io.ox/calendar/settings'
import { addReadyListener } from '@/io.ox/core/events'
import moment from '@/moment'

// define threshold for humanize function
moment.relativeTimeThreshold('s', 60)
moment.relativeTimeThreshold('m', 55)
moment.relativeTimeThreshold('h', 23)
moment.relativeTimeThreshold('d', 29)
moment.relativeTimeThreshold('M', 11)

// fix local function to work with timezone plugin
moment.fn.local = function (keepLocalTime) {
  if (this._isUTC) {
    this.utcOffset(0, keepLocalTime)
    this._isUTC = false
    this._z = moment.defaultZone || null
    if (keepLocalTime) {
      this.subtract((this._z ? this._z.utcOffset(this) : Math.round(this._d.getTimezoneOffset() / 15) * 15) * -1, 'm')
    }
  }
  return this
}

// add aliases for deprecated/unsupported timezones
const timezoneAliases = [
  // unsupported SystemV, without daylight saving time
  'Etc/GMT+4|SystemV/AST4',
  'Etc/GMT+5|SystemV/EST5',
  'Etc/GMT+6|SystemV/CST6',
  'Etc/GMT+7|SystemV/MST7',
  'Etc/GMT+8|SystemV/PST8',
  'Etc/GMT+9|SystemV/YST9',
  'Etc/GMT+10|SystemV/HST10',
  // unsupported SystemV, with daylight saving time
  'America/Halifax|SystemV/AST4ADT', // Atlantic
  'America/Chicago|SystemV/EST5EDT', // Eastern
  'America/New_York|SystemV/CST6CDT', // Central
  'America/Denver|SystemV/MST7MDT', // Mountain
  'America/Los_Angeles|SystemV/PST8PDT', // Pacific
  'America/Anchorage|SystemV/YST9YDT' // Alaska
]

moment.tz.link(timezoneAliases)

// set default timezone
moment.tz.setDefault(coreSettings.getTimezone())

addReadyListener('settings', () => {
  // fix favorites
  let favorites = calendarSettings.get('favoriteTimezones')
  if (!Array.isArray(favorites)) favorites = []
  const replacements = coreSettings.getTimezoneReplacements()
  calendarSettings.set('favoriteTimezones', favorites.map(zone => replacements[zone] ?? zone))
})

export default moment
